/**
 * Per-effect per-frame drawing functions. Each takes the canvas
 * context + shared state + timing/pointer info and advances the
 * particles one frame.
 */
import type { CanvasState, CanvasEffectId, PointerSample } from '../types';
import {
  addSoftSpot,
  drawDiamond,
  drawHeart,
  drawKiss,
  drawPetal,
  drawPolaroid,
  drawSmoothPath,
  paintBackground,
  paintVignette,
} from '../primitives';
import {
  choose,
  easeOutCubic,
  heartInfluence,
  heartPoint,
  lerp,
  random,
  randomStatic,
  rgba,
} from '../utils';
import { makeParticle } from '../types';
import {
  spawnBubbleSplash,
  spawnFlowerBurst,
  spawnSparkHearts,
} from './initializers';

/* Dispatcher — called once per frame from ParticleCanvas. */
export function drawEffect(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  effect: CanvasEffectId,
  time: number,
  dt: number,
  pointer: PointerSample,
  reducedMotion: boolean,
): void {
  ctx.save();
  ctx.setTransform(state.dpr, 0, 0, state.dpr, 0, 0);
  ctx.clearRect(0, 0, state.width, state.height);
  switch (effect) {
    case 'floating-heart-bokeh':
      drawFloatingHeartBokeh(ctx, state, time, dt);
      break;
    case 'rose-petal-cascade':
      drawRosePetalCascade(ctx, state, time, dt);
      break;
    case 'sparkler-trail-hearts':
      drawSparklerTrailHearts(ctx, state, time, dt, pointer, reducedMotion);
      break;
    case 'constellation-connection':
      drawConstellationConnection(ctx, state, time, dt);
      break;
    case 'velvet-dust-motes':
      drawVelvetDustMotes(ctx, state, time, dt);
      break;
    case 'heartbeat-pulse-rings':
      drawHeartbeatPulseRings(ctx, state, time);
      break;
    case 'champagne-bubble-rise':
      drawChampagneBubbleRise(ctx, state, time, dt);
      break;
    case 'firefly-courtship':
      drawFireflyCourtship(ctx, state, time, dt);
      break;
    case 'silk-ribbon-flow':
      drawSilkRibbonFlow(ctx, state, time, dt);
      break;
    case 'diamond-dust-sparkle':
      drawDiamondDustSparkle(ctx, state, time, dt);
      break;
    case 'pheromone-trail':
      drawPheromoneTrail(ctx, state, time, dt);
      break;
    case 'aurora-heart':
      drawAuroraHeart(ctx, state, time);
      break;
    case 'floating-kiss-marks':
      drawFloatingKissMarks(ctx, state, time, dt);
      break;
    case 'memory-fragment-drift':
      drawMemoryFragmentDrift(ctx, state, time, dt);
      break;
    case 'entwined-particle-vines':
      drawEntwinedParticleVines(ctx, state, time, dt);
      break;
  }
  ctx.restore();
}

/* ─── individual effect renderers ─────────────────────────────────── */

export function drawFloatingHeartBokeh(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#1A0A1A', '#2A1024', '#050205');
  addSoftSpot(ctx, w * 0.52, h * 0.36, w * 0.54, 'rgba(255,182,193,0.14)');

  for (const p of state.particles) {
    p.y += p.vy * dt;
    p.x += (p.vx + Math.sin(time * 0.7 + p.phase) * 0.08 * p.depth) * dt;
    p.rot += Math.sin(time * 0.38 + p.seed) * 0.002 * dt;

    if (p.y < -p.size * 2) {
      p.y = h + p.size * 2;
      p.x = random(-w * 0.1, w * 1.1);
    }

    ctx.filter = `blur(${p.blur}px)`;
    ctx.shadowColor = p.color;
    ctx.shadowBlur = 18 * p.depth;
    drawHeart(
      ctx,
      p.x,
      p.y,
      p.size,
      p.color,
      p.alpha * (0.75 + Math.sin(time + p.phase) * 0.15),
      p.rot,
      true,
    );
  }
  ctx.filter = 'none';
  ctx.shadowBlur = 0;
  paintVignette(ctx, w, h, 0.72);
}

export function drawRosePetalCascade(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#1A0508', '#320711', '#080104');
  addSoftSpot(ctx, w * 0.42, h * 0.18, w * 0.5, 'rgba(250,218,221,0.12)');

  for (const p of state.particles) {
    if (p.settled > 0) {
      p.settled += dt;
      p.alpha = Math.max(0, 1 - p.settled / 160);
      if (p.alpha <= 0.02) {
        resetPetal(p, w, h);
      }
    } else {
      const wind =
        Math.sin(time * 0.9 + p.phase) * 0.16 +
        Math.sin(time * 0.33 + p.seed) * 0.1;
      p.vx = p.vx * 0.992 + wind * 0.012 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vr * dt + Math.sin(time + p.phase) * 0.004 * dt;
      p.vr *= 0.996;

      if (p.x < -p.size * 3) p.x = w + p.size * 2;
      if (p.x > w + p.size * 3) p.x = -p.size * 2;
      if (p.y > h - p.size * 1.1) {
        p.y = h - p.size * 1.1;
        p.settled = 1;
        p.vx *= 0.3;
        p.vy = 0;
      }
    }

    drawPetal(ctx, p.x, p.y, p.size, p.color, p.alpha, p.rot, time + p.phase);
  }
  paintVignette(ctx, w, h, 0.82);
}

function resetPetal(
  p: { x: number; y: number; vx: number; vy: number; alpha: number; settled: number },
  w: number,
  h: number,
): void {
  p.x = random(-w * 0.08, w * 1.08);
  p.y = random(-h * 0.3, -20);
  p.vx = random(-0.35, 0.35);
  p.vy = random(0.34, 1.25);
  p.alpha = random(0.5, 0.95);
  p.settled = 0;
}

export function drawSparklerTrailHearts(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
  pointer: PointerSample,
  reducedMotion: boolean,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#110606', '#221009', '#030203');
  addSoftSpot(ctx, w * 0.5, h * 0.44, w * 0.42, 'rgba(255,191,0,0.12)');

  if (!reducedMotion && time - state.lastSpawn > 0.12) {
    const pointerFresh =
      pointer.active && performance.now() - pointer.time < 900;
    const sourceX = pointerFresh
      ? pointer.x
      : w * 0.5 + Math.sin(time * 1.4) * w * 0.14;
    const sourceY = pointerFresh
      ? pointer.y
      : h * 0.45 + Math.cos(time * 1.1) * h * 0.08;
    spawnSparkHearts(state, sourceX, sourceY, pointer.active ? 2 : 1);
    state.lastSpawn = time;
  }

  ctx.globalCompositeOperation = 'screen';
  for (let i = state.sparks.length - 1; i >= 0; i -= 1) {
    const p = state.sparks[i];
    p.life -= dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.vy += 0.018 * dt;
    p.vx *= 0.965;
    p.rot += p.vr * dt;

    const lifeRatio = Math.max(0, p.life / p.maxLife);
    ctx.shadowColor = p.color;
    ctx.shadowBlur = 18 * lifeRatio;
    drawHeart(
      ctx,
      p.x,
      p.y,
      p.size * (0.45 + lifeRatio),
      p.color,
      lifeRatio * 0.9,
      p.rot,
      true,
    );

    ctx.globalAlpha = lifeRatio * 0.8;
    ctx.fillStyle = '#FFF5E6';
    ctx.beginPath();
    ctx.arc(
      p.x - p.vx * 1.8,
      p.y - p.vy * 1.8,
      Math.max(0.8, p.size * 0.12),
      0,
      Math.PI * 2,
    );
    ctx.fill();
    ctx.globalAlpha = 1;

    if (p.life <= 0) state.sparks.splice(i, 1);
  }
  ctx.globalCompositeOperation = 'source-over';
  ctx.shadowBlur = 0;
  paintVignette(ctx, w, h, 0.76);
}

export function drawConstellationConnection(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#0B0D17', '#070817', '#01020A');

  for (const p of state.particles) {
    p.x += p.vx * dt;
    p.y += p.vy * dt;

    if (p.depth > 0) {
      const targetX = p.baseX + Math.sin(time * 0.35 + p.seed) * 12;
      const targetY = p.baseY + Math.cos(time * 0.32 + p.seed) * 10;
      p.vx += (targetX - p.x) * 0.0008 * dt;
      p.vy += (targetY - p.y) * 0.0008 * dt;
    }

    if (p.x < 0 || p.x > w) p.vx *= -1;
    if (p.y < 0 || p.y > h) p.vy *= -1;
  }

  ctx.globalCompositeOperation = 'screen';
  for (let i = 0; i < state.particles.length; i += 1) {
    const a = state.particles[i];
    for (let j = i + 1; j < state.particles.length; j += 1) {
      const b = state.particles[j];
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const distance = Math.hypot(dx, dy);
      if (distance < 82) {
        const alpha =
          (1 - distance / 82) * 0.32 * (a.depth || b.depth ? 1.3 : 0.55);
        ctx.strokeStyle = `rgba(212,175,55,${alpha})`;
        ctx.lineWidth = a.depth || b.depth ? 0.9 : 0.45;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.quadraticCurveTo(
          (a.x + b.x) / 2 + Math.sin(time + a.seed) * 4,
          (a.y + b.y) / 2,
          b.x,
          b.y,
        );
        ctx.stroke();
      }
    }
  }

  for (const p of state.particles) {
    const twinkle = 0.55 + Math.sin(time * 2 + p.phase) * 0.35;
    ctx.shadowColor = p.color;
    ctx.shadowBlur = p.depth > 0 ? 12 : 8;
    ctx.globalAlpha = p.alpha * twinkle;
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
  ctx.globalCompositeOperation = 'source-over';
  paintVignette(ctx, w, h, 0.72);
}

export function drawVelvetDustMotes(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#1C1C1C', '#111111', '#050505');

  const lightX = w * (0.44 + Math.sin(time * 0.18) * 0.2);
  const lightY = h * (0.32 + Math.cos(time * 0.13) * 0.1);
  const radius = w * 0.62;
  const shaft = ctx.createRadialGradient(lightX, lightY, 0, lightX, lightY, radius);
  shaft.addColorStop(0, 'rgba(255,248,231,0.22)');
  shaft.addColorStop(0.45, 'rgba(240,230,140,0.07)');
  shaft.addColorStop(1, 'rgba(255,248,231,0)');
  ctx.fillStyle = shaft;
  ctx.fillRect(0, 0, w, h);

  ctx.globalCompositeOperation = 'screen';
  for (const p of state.particles) {
    p.x += (p.vx + Math.sin(time * 0.32 + p.phase) * 0.018) * dt;
    p.y += (p.vy + Math.cos(time * 0.27 + p.phase) * 0.014) * dt;

    if (p.x < 0) p.x = w;
    if (p.x > w) p.x = 0;
    if (p.y < 0) p.y = h;
    if (p.y > h) p.y = 0;

    const dx = (p.x - lightX) / radius;
    const dy = (p.y - lightY) / (radius * 1.2);
    const visibility = Math.max(0, 1 - Math.hypot(dx, dy));
    ctx.globalAlpha = visibility * visibility * p.alpha;
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size * (0.8 + visibility), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  paintVignette(ctx, w, h, 0.82);
}

export function drawHeartbeatPulseRings(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#150006', '#26000D', '#030002');
  addSoftSpot(ctx, w * 0.5, h * 0.43, w * 0.5, 'rgba(255,20,147,0.16)');

  ctx.globalCompositeOperation = 'screen';
  for (let i = 0; i < 6; i += 1) {
    const age = (time + i * 0.46) % 3.6;
    const ratio = age / 3.6;
    const pulse = Math.sin((time * 2.2 + i) * Math.PI) > 0 ? 1.05 : 0.92;
    const size = w * (0.12 + ratio * 0.78) * pulse;
    const alpha = Math.pow(1 - ratio, 1.8) * 0.78;
    ctx.strokeStyle = `rgba(255,20,147,${alpha})`;
    ctx.lineWidth = 1.2 + (1 - ratio) * 2.4;
    ctx.shadowColor = '#FF1493';
    ctx.shadowBlur = 24 * alpha;
    drawHeart(ctx, w * 0.5, h * 0.43, size, '#FF1493', alpha, 0, false);
  }

  const corePulse = 0.86 + Math.sin(time * 6.2) * 0.14;
  ctx.shadowColor = '#FF1493';
  ctx.shadowBlur = 34;
  drawHeart(ctx, w * 0.5, h * 0.43, w * 0.11 * corePulse, '#FF1493', 0.9, 0, true);
  ctx.shadowBlur = 0;
  ctx.globalCompositeOperation = 'source-over';
  paintVignette(ctx, w, h, 0.78);
}

export function drawChampagneBubbleRise(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#171004', '#33210C', '#040302');
  addSoftSpot(ctx, w * 0.52, h * 0.25, w * 0.62, 'rgba(247,231,206,0.12)');

  ctx.globalCompositeOperation = 'screen';
  for (const p of state.particles) {
    p.y -= p.vy * dt;
    p.x = p.baseX + Math.sin(time * (0.8 + p.size * 0.05) + p.phase) * p.size * 1.45;
    if (p.y < h * 0.08) {
      spawnBubbleSplash(state, p.x, h * 0.08, p.color);
      p.y = h + random(10, 120);
      p.baseX = random(w * 0.08, w * 0.92);
    }

    ctx.globalAlpha = p.alpha;
    ctx.strokeStyle = p.color;
    ctx.lineWidth = 0.8;
    ctx.shadowColor = p.color;
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.38)';
    ctx.beginPath();
    ctx.arc(p.x - p.size * 0.25, p.y - p.size * 0.3, Math.max(0.6, p.size * 0.2), 0, Math.PI * 2);
    ctx.fill();
  }

  for (let i = state.sparks.length - 1; i >= 0; i -= 1) {
    const p = state.sparks[i];
    p.life -= dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    const ratio = Math.max(0, p.life / p.maxLife);
    ctx.globalAlpha = ratio * 0.8;
    ctx.strokeStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size * ratio, 0, Math.PI * 2);
    ctx.stroke();
    if (p.life <= 0) state.sparks.splice(i, 1);
  }
  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
  ctx.globalCompositeOperation = 'source-over';
  paintVignette(ctx, w, h, 0.76);
}

export function drawFireflyCourtship(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#05140A', '#071008', '#010402');
  addSoftSpot(ctx, w * 0.5, h * 0.55, w * 0.6, 'rgba(204,255,0,0.07)');

  for (let i = 0; i < state.particles.length; i += 1) {
    const a = state.particles[i];
    a.vx += Math.sin(time * 0.55 + a.seed) * 0.002 * dt;
    a.vy += Math.cos(time * 0.45 + a.seed) * 0.002 * dt;
    for (let j = i + 1; j < state.particles.length; j += 1) {
      const b = state.particles[j];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const distance = Math.hypot(dx, dy);
      if (distance > 0 && distance < 100) {
        const force = (1 - distance / 100) * 0.004 * dt;
        a.vx += (dx / distance) * force;
        a.vy += (dy / distance) * force;
        b.vx -= (dx / distance) * force;
        b.vy -= (dy / distance) * force;
        const phaseAverage = (a.phase + b.phase) / 2;
        a.phase += (phaseAverage - a.phase) * 0.025 * dt;
        b.phase += (phaseAverage - b.phase) * 0.025 * dt;
      }
    }
  }

  ctx.globalCompositeOperation = 'screen';
  for (const p of state.particles) {
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.vx *= 0.985;
    p.vy *= 0.985;
    if (p.x < 12 || p.x > w - 12) p.vx *= -1;
    if (p.y < 24 || p.y > h - 90) p.vy *= -1;

    const pulse = Math.pow((Math.sin(time * 2.6 + p.phase) + 1) / 2, 2.2);
    const glow = 0.18 + pulse * 0.9;
    ctx.shadowColor = p.color;
    ctx.shadowBlur = 24 * glow;
    ctx.fillStyle = p.color;
    ctx.globalAlpha = glow;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size * (0.6 + glow), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
  ctx.globalCompositeOperation = 'source-over';
  paintVignette(ctx, w, h, 0.8);
}

export function drawSilkRibbonFlow(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#0D0D0D', '#160C12', '#030303');
  addSoftSpot(ctx, w * 0.5, h * 0.44, w * 0.5, 'rgba(230,184,184,0.08)');

  ctx.globalCompositeOperation = 'screen';
  state.ribbons.forEach((ribbon, ribbonIndex) => {
    const forming = Math.max(0, Math.sin(time * 0.42 + ribbon.phase) - 0.42) / 0.58;
    ribbon.points.forEach((point, pointIndex) => {
      const u = pointIndex / (ribbon.points.length - 1);
      const openX = w * (u * 1.3 - 0.15);
      const openY =
        h * (0.34 + ribbonIndex * 0.12) +
        Math.sin(u * Math.PI * 2.2 + time * (0.8 + ribbonIndex * 0.12) + ribbon.phase) *
          h *
          0.08;
      const heart = heartPoint(u * Math.PI * 2 + ribbon.phase * 0.12);
      const heartX = w * 0.5 + heart.x * w * (0.22 + ribbonIndex * 0.018);
      const heartY = h * 0.43 - heart.y * h * (0.15 + ribbonIndex * 0.012);
      const targetX = lerp(openX, heartX, forming);
      const targetY = lerp(openY, heartY, forming);
      point.x += (targetX - point.x) * 0.055 * dt;
      point.y += (targetY - point.y) * 0.055 * dt;
    });

    ctx.shadowColor = ribbon.colorB;
    ctx.shadowBlur = 24;
    ctx.lineWidth = ribbon.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const gradient = ctx.createLinearGradient(0, h * 0.3, w, h * 0.55);
    gradient.addColorStop(0, rgba(ribbon.colorA, 0.08));
    gradient.addColorStop(0.45, rgba(ribbon.colorB, 0.32));
    gradient.addColorStop(1, rgba(ribbon.colorA, 0.06));
    ctx.strokeStyle = gradient;
    drawSmoothPath(ctx, ribbon.points);
  });
  ctx.shadowBlur = 0;
  ctx.globalCompositeOperation = 'source-over';
  paintVignette(ctx, w, h, 0.78);
}

export function drawDiamondDustSparkle(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#07090E', '#0B0A13', '#020203');
  addSoftSpot(ctx, w * 0.5, h * 0.4, w * 0.52, 'rgba(176,224,230,0.14)');

  ctx.globalCompositeOperation = 'screen';
  for (const p of state.particles) {
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.rot += p.vr * dt;
    const twinkle = Math.pow((Math.sin(time * (2.2 + p.depth) + p.phase) + 1) / 2, 5);
    const alpha = 0.08 + twinkle * p.alpha;
    if (p.x < -10) p.x = w + 10;
    if (p.x > w + 10) p.x = -10;
    if (p.y < -10) p.y = h + 10;
    if (p.y > h + 10) p.y = -10;

    ctx.shadowColor = p.color;
    ctx.shadowBlur = 12 * alpha;
    drawDiamond(ctx, p.x + 1, p.y, p.size, '#FF69B4', alpha * 0.45, p.rot);
    drawDiamond(ctx, p.x - 1, p.y, p.size, '#B0E0E6', alpha * 0.45, p.rot);
    drawDiamond(ctx, p.x, p.y, p.size * 0.86, p.color, alpha, p.rot);
  }
  ctx.shadowBlur = 0;
  ctx.globalCompositeOperation = 'source-over';
  paintVignette(ctx, w, h, 0.76);
}

export function drawPheromoneTrail(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
): void {
  const { width: w, height: h } = state;
  const trailCtx = state.trailCtx;
  paintBackground(ctx, w, h, '#2D0A1F', '#190513', '#050104');

  if (trailCtx && state.trailCanvas) {
    trailCtx.globalCompositeOperation = 'destination-out';
    trailCtx.fillStyle = 'rgba(0,0,0,0.035)';
    trailCtx.fillRect(0, 0, w, h);
    trailCtx.globalCompositeOperation = 'source-over';
  }

  for (const p of state.particles) {
    p.vx += Math.sin(time * 0.48 + p.phase) * 0.002 * dt;
    p.vy += Math.cos(time * 0.41 + p.phase) * 0.002 * dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    if (p.x < 10 || p.x > w - 10) p.vx *= -1;
    if (p.y < 30 || p.y > h - 120) p.vy *= -1;
    if (trailCtx) {
      const stamp = trailCtx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 4.5);
      stamp.addColorStop(0, rgba(p.color, 0.055));
      stamp.addColorStop(0.45, rgba('#FFB45E', 0.018));
      stamp.addColorStop(1, 'rgba(255,180,94,0)');
      trailCtx.fillStyle = stamp;
      trailCtx.fillRect(p.x - p.size * 5, p.y - p.size * 5, p.size * 10, p.size * 10);
    }
  }

  for (let i = 0; i < state.particles.length; i += 1) {
    const a = state.particles[i];
    for (let j = i + 1; j < state.particles.length; j += 1) {
      const b = state.particles[j];
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      if (
        distance < 34 &&
        state.blooms.length < 42 &&
        Math.random() < 0.025 * dt
      ) {
        const bloom = makeParticle({
          x: (a.x + b.x) / 2,
          y: (a.y + b.y) / 2,
          size: random(18, 42),
          alpha: 1,
          color: '#FFDAB9',
          life: 34,
          maxLife: 34,
        });
        state.blooms.push(bloom);
      }
    }
  }

  if (state.trailCanvas) {
    ctx.globalCompositeOperation = 'screen';
    ctx.drawImage(state.trailCanvas, 0, 0, w, h);
  }

  for (let i = state.blooms.length - 1; i >= 0; i -= 1) {
    const bloom = state.blooms[i];
    bloom.life -= dt;
    const ratio = Math.max(0, bloom.life / bloom.maxLife);
    addSoftSpot(ctx, bloom.x, bloom.y, bloom.size * (1.6 - ratio), rgba(bloom.color, ratio * 0.22));
    if (bloom.life <= 0) state.blooms.splice(i, 1);
  }

  ctx.globalCompositeOperation = 'source-over';
  paintVignette(ctx, w, h, 0.76);
}

export function drawAuroraHeart(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#02050A', '#080518', '#010102');
  addSoftSpot(ctx, w * 0.5, h * 0.35, w * 0.7, 'rgba(138,43,226,0.08)');

  ctx.globalCompositeOperation = 'screen';
  ctx.filter = 'blur(0.6px)';
  const colors = [
    ['rgba(57,255,20,0)', 'rgba(57,255,20,0.22)', 'rgba(255,0,127,0.02)'],
    ['rgba(138,43,226,0)', 'rgba(138,43,226,0.2)', 'rgba(57,255,20,0.03)'],
    ['rgba(255,0,127,0)', 'rgba(255,0,127,0.18)', 'rgba(138,43,226,0.02)'],
  ];

  for (let layer = 0; layer < 7; layer += 1) {
    const gradient = ctx.createLinearGradient(0, h * 0.12, 0, h * 0.84);
    const palette = colors[layer % colors.length];
    gradient.addColorStop(0, palette[0]);
    gradient.addColorStop(0.4, palette[1]);
    gradient.addColorStop(1, palette[2]);
    ctx.fillStyle = gradient;
    ctx.beginPath();

    for (let x = -20; x <= w + 20; x += 8) {
      const nx = (x - w * 0.5) / (w * 0.25);
      const baseY = h * (0.24 + layer * 0.055);
      const wave = Math.sin(x * 0.018 + time * (0.8 + layer * 0.05) + layer) * h * 0.035;
      const waveTwo = Math.sin(x * 0.041 - time * 0.55 + layer * 1.4) * h * 0.022;
      const heartLift = heartInfluence(nx, layer / 7, time) * h * 0.045;
      const y = baseY + wave + waveTwo - heartLift;
      if (x === -20) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }

    ctx.lineTo(w + 20, h * (0.86 - layer * 0.018));
    ctx.lineTo(-20, h * (0.86 - layer * 0.018));
    ctx.closePath();
    ctx.globalAlpha = 0.82;
    ctx.fill();
  }
  ctx.filter = 'none';
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';

  ctx.globalCompositeOperation = 'screen';
  const pulse = 0.35 + Math.sin(time * 0.8) * 0.15;
  ctx.strokeStyle = `rgba(255,0,127,${pulse})`;
  ctx.lineWidth = 1.4;
  ctx.shadowColor = '#FF007F';
  ctx.shadowBlur = 20;
  drawHeart(ctx, w * 0.5, h * 0.42, w * 0.42, '#FF007F', pulse, 0, false);
  ctx.shadowBlur = 0;
  ctx.globalCompositeOperation = 'source-over';
  paintVignette(ctx, w, h, 0.74);
}

export function drawFloatingKissMarks(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#190208', '#230511', '#050104');
  addSoftSpot(ctx, w * 0.45, h * 0.28, w * 0.48, 'rgba(196,30,58,0.12)');

  for (const p of state.particles) {
    p.y += p.vy * dt;
    p.x += (p.vx + Math.sin(time * 0.8 + p.phase) * 0.06) * dt;
    p.rot += p.vr * dt;
    if (p.y < -p.size * 2) {
      p.y = h + p.size * 2;
      p.x = random(w * 0.08, w * 0.92);
    }
    ctx.filter = `blur(${p.blur}px)`;
    drawKiss(ctx, p.x, p.y, p.size, p.color, p.alpha * (0.78 + Math.sin(time + p.phase) * 0.12), p.rot);
  }
  ctx.filter = 'none';
  paintVignette(ctx, w, h, 0.8);
}

export function drawMemoryFragmentDrift(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#3C1414', '#1A0705', '#050202');
  addSoftSpot(ctx, w * 0.5, h * 0.44, w * 0.52, 'rgba(244,230,207,0.09)');

  const centerX = w * 0.5;
  const centerY = h * 0.43;
  for (const fragment of state.fragments) {
    fragment.x += (fragment.vx + Math.sin(time * 0.16 + fragment.seed) * 0.06) * dt;
    fragment.y += (fragment.vy + Math.cos(time * 0.13 + fragment.seed) * 0.035) * dt;
    fragment.rot += fragment.vr * dt;

    if (
      fragment.x < -fragment.w * 1.5 ||
      fragment.x > w + fragment.w * 1.5 ||
      fragment.y < -fragment.h * 1.5
    ) {
      fragment.x = random(-w * 0.15, w * 1.05);
      fragment.y = h + random(20, 140);
    }

    const distance = Math.hypot(fragment.x - centerX, fragment.y - centerY);
    const focus = Math.max(0, 1 - distance / (w * 0.44));
    const blur = 5.2 * (1 - focus) + 0.2;
    ctx.filter = `blur(${blur}px) sepia(${0.35 - focus * 0.25})`;
    ctx.globalAlpha = 0.34 + focus * 0.66;
    drawPolaroid(ctx, fragment, time);
  }
  ctx.globalAlpha = 1;
  ctx.filter = 'none';
  paintVignette(ctx, w, h, 0.84);
}

export function drawEntwinedParticleVines(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  time: number,
  dt: number,
): void {
  const { width: w, height: h } = state;
  paintBackground(ctx, w, h, '#05040A', '#070617', '#020103');
  addSoftSpot(ctx, w * 0.5, h * 0.48, w * 0.6, 'rgba(255,217,61,0.09)');

  const cycle = (time * 0.12) % 1.28;
  const growth = Math.min(1, cycle / 0.98);
  ctx.globalCompositeOperation = 'screen';

  for (const p of state.particles) {
    const localProgress = Math.min(1, Math.max(0, growth - p.depth * 0.28 + 0.12));
    const eased = easeOutCubic(localProgress);
    const startX = p.side < 0 ? -w * 0.08 : w * 1.08;
    const endX = w * 0.5 + p.side * randomStatic(p.seed) * 18;
    const wave = Math.sin(eased * Math.PI * 3.2 + p.phase + time * 1.4) * h * 0.08;
    const vineOffset = Math.sin(eased * Math.PI * 7 + p.phase) * h * 0.035;
    const targetX = lerp(startX, endX, eased);
    const targetY = h * 0.5 + wave * (1 - eased * 0.42) + vineOffset;
    p.x += (targetX - p.x) * 0.1 * dt;
    p.y += (targetY - p.y) * 0.1 * dt;

    const visible = localProgress > 0.02 ? Math.min(1, localProgress * 2.5) : 0;
    if (visible > 0) {
      ctx.shadowColor = p.color;
      ctx.shadowBlur = 12 * visible;
      ctx.globalAlpha = visible * p.alpha;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * (0.8 + visible * 0.7), 0, Math.PI * 2);
      ctx.fill();
    }
  }

  for (let i = 0; i < state.particles.length; i += 1) {
    const a = state.particles[i];
    for (let j = i + 1; j < state.particles.length; j += 1) {
      const b = state.particles[j];
      if (a.side === b.side) {
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (distance < 26) {
          ctx.strokeStyle =
            a.side < 0
              ? 'rgba(255,217,61,0.12)'
              : 'rgba(168,85,247,0.12)';
          ctx.lineWidth = 0.7;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
    }
  }

  if (growth > 0.86 && time - state.lastSpawn > 0.08 && state.blooms.length < 120) {
    spawnFlowerBurst(state, w * 0.5 + random(-10, 10), h * 0.5 + random(-18, 18), 7);
    state.lastSpawn = time;
  }

  for (let i = state.blooms.length - 1; i >= 0; i -= 1) {
    const bloom = state.blooms[i];
    bloom.life -= dt;
    bloom.x += bloom.vx * dt;
    bloom.y += bloom.vy * dt;
    bloom.rot += bloom.vr * dt;
    const ratio = Math.max(0, bloom.life / bloom.maxLife);
    ctx.shadowColor = bloom.color;
    ctx.shadowBlur = 20 * ratio;
    drawPetal(
      ctx,
      bloom.x,
      bloom.y,
      bloom.size * (1.25 - ratio * 0.3),
      bloom.color,
      ratio,
      bloom.rot,
      time + bloom.phase,
    );
    if (bloom.life <= 0) state.blooms.splice(i, 1);
  }

  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
  ctx.globalCompositeOperation = 'source-over';
  paintVignette(ctx, w, h, 0.77);
}
